import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { authService } from '../services/auth-service';
import {
  User,
  LoginCredentials,
  RegisterData,
  Organization,
  SubscriptionInfo,
  AuthResponse,
  Portal,
} from '../types/auth.types';
import { storage } from '@/lib/utils/storage';
import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  ACTIVE_ORG_KEY,
  ORGANIZATIONS_KEY,
  PERMISSIONS_KEY,
  MENU_ITEMS_KEY,
  LOCKED_MENU_ITEMS_KEY,
  AUTH_PERSIST_KEY,
  IMPERSONATION_KEY,
} from '@/lib/utils/constants';
import { apiClient } from '@/lib/api';
import { ENDPOINTS } from '@/lib/api/endpoints';
import { Permission } from '@/types';
import { setGlobalDecimalPlaces } from '@/lib/utils/format';
import { guardarTechoDeLaEmpresa } from '../tenant-modules';
import { guardarEmpresaDemo } from '@/features/mini-prixia/empresa-demo';
import {
  clavePorPortal,
  leerPorPortal,
  slugDeLaUrlActual,
  slugEfectivo,
  slugDeLaClave,
  guardarUltimoPortal,
  olvidarUltimoPortal,
} from '@/lib/utils/portal-storage';

/**
 * Pide `/me` una sola vez por carga cuando se consulta un permiso sin tener la lista del
 * servidor. `hasPermission` se llama al dibujar: el pedido sale en una microtarea, nunca
 * adentro del render, y no se repite mientras no llegue (evita el bucle).
 */
let permisosPedidos = false;
function pedirPermisosFrescos(get: () => AuthState): void {
  if (permisosPedidos) return;
  permisosPedidos = true;
  queueMicrotask(() => {
    void Promise.resolve(get().checkAuth())
      .catch(() => undefined)
      .finally(() => {
        // Si el /me no trajo la lista (red caída, portal por canjear), la próxima consulta
        // lo vuelve a pedir en vez de dejar la sesión negando todo hasta recargar.
        if (!get().permisosCargados) permisosPedidos = false;
      });
  });
}

/**
 * Estado de autenticación
 */
interface ImpersonationBackup {
  user: User;
  token: string;
  refreshToken: string;
  permissions: string[];
  menuItems: string[];
  lockedMenuItems: string[];
  organizations: Organization[];
  activeOrganizationId: string | null;
  subscription: SubscriptionInfo | null;
  /**
   * Bajo qué empresa estaba la sesión del admin ANTES de impersonar.
   *
   * 🔴 Sin esto, salir del modo soporte restaura el token del admin bajo la
   * empresa donde uno terminó —la de la persona impersonada— y deja el token
   * impersonado vivo en la empresa original. Es el defecto que
   * `exitImpersonation` tenía anotado como frente abierto: no se arreglaba
   * desde ahí porque faltaba el dato de dónde venía.
   */
  slugDelAdmin: string | null;
  /**
   * Valor crudo de `empresa_demo:<slugDelAdmin>` antes de impersonar (`null` = no existía).
   * Si la respuesta no trae empresa, el destino cae en la del admin y `abrirSesion` le
   * pisaría la marca demo; `exitImpersonation` la restaura. `undefined` en respaldos viejos.
   */
  demoDelAdmin?: string | null;
}

const EMPRESA_DEMO_KEY = 'empresa_demo';
const FUE_DEMO_PREFIJO = 'empresa_fue_demo';

interface AuthState {
  // Estado
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: string[];
  /**
   * Si la lista de `permissions` LLEGÓ del servidor (login, canje de portal o `/me`),
   * aunque venga vacía. Una lista vacía cargada es «este rol no puede nada», no «no sé»:
   * sin esta marca la pantalla caía a una tabla por cargo y mostraba botones que el
   * servidor rechaza. Falla cerrado: sin marca, `hasPermission` niega y pide `/me`.
   */
  permisosCargados: boolean;
  menuItems: string[];
  lockedMenuItems: string[];
  organizations: Organization[];
  activeOrganizationId: string | null;
  subscription: SubscriptionInfo | null;
  isImpersonating: boolean;
  /** Las empresas a las que esta persona puede entrar. */
  portales: Portal[];
  /** Canjeable por una sesión en uno de los `portales`; dura 10 minutos. */
  portalToken: string | null;
  /** El slug de la empresa activa. Lo reconcilia `<PortalGuard>` contra la URL. */
  portalActivo: string | null;

  // Acciones
  login: (credentials: LoginCredentials) => Promise<void>;
  acceptInvitation: (
    token: string,
    data: { firstName: string; lastName: string; email: string; password: string }
  ) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshTokenAction: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User) => void;
  clearError: () => void;
  switchOrganization: (orgId: string | null) => void;
  /**
   * Abre la sesión de esa persona, bajo SU empresa, y devuelve el slug de esa
   * empresa para que quien llama sepa a dónde navegar. Ver el comentario de la
   * implementación: si el destino no lleva empresa, el defecto vuelve entero.
   */
  impersonate: (userId: string) => Promise<string | null>;
  exitImpersonation: () => void;
  /** Canjea el portal elegido y abre sesión ahí — misma secuencia que `login`. */
  elegirPortal: (slug: string) => Promise<void>;
  /** Vuelve a pedir la lista de portales al backend. */
  refrescarPortales: () => Promise<void>;

  // Helpers
  hasPermission: (permission: Permission) => boolean;
  canSeeMenu: (menuKey: string) => boolean;
  isModuleLocked: (menuKey: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => {
        /**
         * Todo lo que abre una sesión real, salvo el estado del picker de
         * portal.
         *
         * 🔴 Es el ÚNICO lugar que abre una sesión de empresa, y su único
         * llamador es el canje (`elegirPortal`, más abajo). Se extrajo cuando
         * el ingreso todavía abría sesión también y las dos puertas tenían
         * que dejar todo EXACTAMENTE igual —las ocho claves de storage, el
         * techo de módulos de la empresa, la organización activa y los
         * decimales del tenant—; ahora que el ingreso ya no abre sesión, sigue
         * separada por la misma razón dada al revés: cualquier puerta nueva
         * (una que entre por un link, por ejemplo) tiene que llamar acá y no
         * copiar el bloque. Los unitarios mockean el servicio y no ven el
         * storage, así que una clave olvidada no la nota ningún test.
         *
         * Lo que NO entra acá: `portales` / `portalToken` / `portalActivo`.
         * El canje no los recibe porque no cambian al cambiar de empresa, y
         * `portalActivo` lo fija el propio canje con el slug que eligió.
         *
         * 🔴 `slug` — la empresa DE ESTA sesión, no la de la URL actual.
         * `token`, `refreshToken` y la organización activa se guardan bajo una
         * clave por-empresa (`clavePorPortal`, en `lib/utils/portal-storage`)
         * para que el interceptor de Axios, que lee esas claves en cada
         * request desde afuera de React, nunca le sirva a una pestaña el
         * token de otra empresa. Se recibe como parámetro explícito — no se
         * lee de `window.location.pathname` acá adentro — porque cuando el
         * canje corre desde el ingreso la URL todavía dice `/login` o
         * `/portales` (la redirección a `/<slug>` pasa recién después): si se
         * leyera de la URL, el token quedaría escrito bajo la clave pública y
         * la pestaña nueva en `/<slug>` no lo encontraría.
         */
        const abrirSesion = (response: AuthResponse, slug: string | null, esSoporte: boolean) => {
          storage.set(clavePorPortal(TOKEN_KEY, slug), response.token);
          storage.set(clavePorPortal(REFRESH_TOKEN_KEY, slug), response.refreshToken);
          if (slug) {
            /*
              🔴 Migración, y la mitad de la defensa contra el peor defecto de
              este frente. La clave global es de antes de esta rama; en cuanto
              existe una sesión por-empresa de verdad, esa copia es peso
              muerto Y un peligro: `slugDeLaClave` prefiere la global sobre el
              puntero a la última empresa (a propósito, para que la invitación
              de una persona NUEVA no herede la sesión de la anterior), así
              que un token viejo ahí le ganaría al que se acaba de escribir
              acá. Borrarla deja el paso 2 de esa resolución con un solo
              significado posible: "`register` o `acceptInvitation` acaban de
              escribir esto".

              Y el puntero, para que una URL sin empresa (la landing, un
              bookmark viejo) encuentre esta sesión — ver `portal-storage.ts`.
            */
            storage.remove(TOKEN_KEY);
            storage.remove(REFRESH_TOKEN_KEY);
            storage.remove(ACTIVE_ORG_KEY);
            guardarUltimoPortal(slug);
          }
          storage.set(USER_KEY, response.user);
          storage.set(PERMISSIONS_KEY, response.permissions || []);
          storage.set(MENU_ITEMS_KEY, response.menuItems || []);
          guardarTechoDeLaEmpresa((response as any).tenantModules);
          guardarEmpresaDemo((response as any).esDemo, slug, esSoporte);
          storage.set(LOCKED_MENU_ITEMS_KEY, response.lockedMenuItems || []);

          const orgs = response.organizations || [];
          storage.set(ORGANIZATIONS_KEY, orgs);

          // Auto-seleccionar primera org si no hay ninguna guardada, o si la
          // guardada ya no es válida en esta empresa.
          const savedOrgId = storage.get<string>(clavePorPortal(ACTIVE_ORG_KEY, slug));
          const validOrgId =
            savedOrgId && orgs.some((o) => o.id === savedOrgId)
              ? savedOrgId
              : orgs.length > 0
                ? orgs[0].id
                : null;
          if (validOrgId) {
            storage.set(clavePorPortal(ACTIVE_ORG_KEY, slug), validOrgId);
          } else {
            // Sin organización propia no se hereda la del usuario anterior:
            // el interceptor mandaría ese id en X-Organization-Id y el
            // backend respondería 403.
            storage.remove(clavePorPortal(ACTIVE_ORG_KEY, slug));
          }

          // Aplicar configuración de decimales del tenant
          if (response.user?.tenant?.decimalPlaces != null) {
            setGlobalDecimalPlaces(response.user.tenant.decimalPlaces);
          }

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken,
            permissions: response.permissions || [],
            permisosCargados: true,
            menuItems: response.menuItems || [],
            lockedMenuItems: response.lockedMenuItems || [],
            organizations: orgs,
            activeOrganizationId: validOrgId,
            subscription: response.subscription || null,
            isAuthenticated: true,
          });
        };

        return {
          // Estado inicial
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          permissions: [],
          permisosCargados: false,
          menuItems: [],
          lockedMenuItems: [],
          organizations: [],
          activeOrganizationId: null,
          subscription: null,
          isImpersonating: false,
          portales: [],
          portalToken: null,
          portalActivo: null,

          /**
           * Login
           */
          login: async (credentials) => {
            try {
              set({ isLoading: true, error: null });

              const response = await authService.login(credentials);

              /*
                🔴 El ingreso NO abre sesión. Acá había un `abrirSesion(...)`,
                y desde que `/login` dejó de devolver `token` esa llamada
                escribiría `undefined` en las mismas claves de storage que el
                interceptor de Axios lee en cada pedido, y dejaría
                `isAuthenticated: true`: la aplicación creyéndose adentro con
                una credencial que no existe. La sesión la abre `elegirPortal`
                —el canje— y nada más.

                Queda sólo lo que la pantalla de elección necesita: quién es,
                a qué empresas puede entrar, y el token de 10 minutos para
                canjear. Ninguna clave de storage: no hay sesión que guardar.
              */
              set({
                user: response.user,
                portales: response.portals ?? [],
                portalToken: response.portalToken ?? null,
                // Ninguna empresa activa todavía: eso lo decide el canje.
                portalActivo: null,
                isLoading: false,
                error: null,
              });

              // Si el usuario ya completó el onboarding en el servidor, sincronizar estado local
              if ((response.user as any).onboardingCompleted) {
                const { useOnboardingStore } =
                  await import('@/features/onboarding/store/onboarding-store');
                useOnboardingStore.setState({ completed: true });
              }
            } catch (error: any) {
              const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                'Error al iniciar sesión';
              set({
                error: errorMessage,
                isLoading: false,
                isAuthenticated: false,
              });
              throw error;
            }
          },

          /**
           * Canjea el portal elegido en el picker por una sesión real ahí.
           *
           * Es la única puerta a la aplicación: el ingreso ya no abre sesión.
           * Usa `abrirSesion` y no un `set` propio porque si sólo guardara el
           * token, la app entraría sin permisos, sin menú y sin
           * organizaciones — ver la nota de `abrirSesion` arriba.
           */
          elegirPortal: async (slug) => {
            const { portalToken } = get();
            // `portalToken` puede ser `null` tras recargar la página (dura 10
            // minutos): entonces viaja el de sesión, que el canje también acepta.
            const response = await authService.elegirPortal(slug, portalToken ?? undefined);
            /*
              En «Entrar como» la página se recarga y `PortalGuard` vuelve a canjear:
              con el token de soporte no se anotan las marcas de conversión demo.
            */
            abrirSesion(response, slug, get().isImpersonating);
            set({ portalActivo: slug, portalToken: null });
          },

          /** Vuelve a pedir los portales — la lista que trajo el ingreso ya pudo quedar vieja. */
          refrescarPortales: async () => {
            set({ portales: await authService.misPortales() });
          },

          /**
           * Registro vía link de invitación (auto-login). Espeja el flujo de login.
           */
          acceptInvitation: async (token, data) => {
            try {
              set({ isLoading: true, error: null });

              const response = await authService.acceptInvitation(token, data);

              storage.set(TOKEN_KEY, response.token);
              storage.set(REFRESH_TOKEN_KEY, response.refreshToken);
              storage.set(USER_KEY, response.user);
              storage.set(PERMISSIONS_KEY, response.permissions || []);
              storage.set(MENU_ITEMS_KEY, response.menuItems || []);
              guardarTechoDeLaEmpresa((response as any).tenantModules);
              guardarEmpresaDemo((response as any).esDemo, null);

              const orgs = response.organizations || [];
              storage.set(ORGANIZATIONS_KEY, orgs);
              const validOrgId = orgs.length > 0 ? orgs[0].id : null;
              if (validOrgId) storage.set(ACTIVE_ORG_KEY, validOrgId);
              else storage.remove(ACTIVE_ORG_KEY);

              if (response.user?.tenant?.decimalPlaces != null) {
                setGlobalDecimalPlaces(response.user.tenant.decimalPlaces);
              }

              set({
                user: response.user,
                token: response.token,
                refreshToken: response.refreshToken,
                permissions: response.permissions || [],
                permisosCargados: true,
                menuItems: response.menuItems || [],
                organizations: orgs,
                activeOrganizationId: validOrgId,
                subscription: response.subscription || null,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
            } catch (error: any) {
              const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                'No se pudo completar el registro';
              set({ error: errorMessage, isLoading: false, isAuthenticated: false });
              throw error;
            }
          },

          /**
           * Registro
           */
          register: async (data) => {
            try {
              set({ isLoading: true, error: null });

              const response = await authService.register(data);

              // Guardar en storage
              storage.set(TOKEN_KEY, response.token);
              storage.set(REFRESH_TOKEN_KEY, response.refreshToken);
              storage.set(USER_KEY, response.user);

              set({
                user: response.user,
                token: response.token,
                refreshToken: response.refreshToken,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              });
            } catch (error: any) {
              const errorMessage =
                error.response?.data?.error ||
                error.response?.data?.message ||
                'Error al registrar';
              set({
                error: errorMessage,
                isLoading: false,
                isAuthenticated: false,
              });
              throw error;
            }
          },

          /**
           * Logout
           */
          logout: () => {
            // Intentar logout en el servidor (fire and forget)
            authService.logout().catch(() => {});

            // Preservar datos que no deben resetearse con el logout
            const onboardingData = window.localStorage.getItem('onboarding-storage');
            // La marca «fue demo» sobrevive al cierre de sesión (puede volver días después).
            const fueDemo: Array<[string, string]> = [];
            for (let i = 0; i < window.localStorage.length; i++) {
              const clave = window.localStorage.key(i);
              const valor = clave ? window.localStorage.getItem(clave) : null;
              if (clave && valor !== null && clave.startsWith(FUE_DEMO_PREFIJO)) {
                fueDemo.push([clave, valor]);
              }
            }

            // Limpiar storage
            storage.clear();

            // Restaurar datos no-auth
            if (onboardingData) window.localStorage.setItem('onboarding-storage', onboardingData);
            fueDemo.forEach(([clave, valor]) => window.localStorage.setItem(clave, valor));

            // Limpiar estado
            permisosPedidos = false;
            set({
              user: null,
              token: null,
              refreshToken: null,
              isAuthenticated: false,
              error: null,
              permissions: [],
              permisosCargados: false,
              menuItems: [],
              lockedMenuItems: [],
              organizations: [],
              activeOrganizationId: null,
              subscription: null,
              isImpersonating: false,
              // Sin esto, `/portales` (ruta pública) seguía listando las
              // empresas de la persona anterior después de salir, y un
              // `portalToken` de 10 minutos todavía vivo abriría sesión para
              // ESA persona (el token trae su propio `userId`).
              portales: [],
              portalToken: null,
              portalActivo: null,
            });
            storage.remove(IMPERSONATION_KEY);
            // Ya lo borra el `storage.clear()` de arriba; se repite explícito
            // para que el olvido no dependa de que el clear siga siendo total.
            olvidarUltimoPortal();
          },

          /**
           * Refresh token
           */
          refreshTokenAction: async () => {
            try {
              const currentRefreshToken = get().refreshToken;

              if (!currentRefreshToken) {
                throw new Error('No refresh token available');
              }

              const response = await authService.refreshToken(currentRefreshToken);

              /*
                El invariante de `portal-storage.ts`: el token nuevo va a la
                clave que el interceptor va a LEER. `slugEfectivo()` y no
                `portalActivo` porque `portalActivo` no se persiste — en una
                pestaña recién cargada es `null`, y con `null`
                `clavePorPortal` devuelve la clave GLOBAL, que ya no es de
                donde el interceptor saca el token de una sesión de portal.
                (Sin llamador hoy en `src/`; si alguien la usa mañana tiene
                que dejar la sesión consistente.)
              */
              storage.set(clavePorPortal(TOKEN_KEY, slugEfectivo()), response.token);

              set({
                token: response.token,
              });
            } catch (error) {
              // Si el refresh falla, logout
              get().logout();
              throw error;
            }
          },

          /**
           * Verificar autenticación al cargar la app
           */
          checkAuth: async () => {
            try {
              /*
                Si la URL nombra una empresa distinta de `portalActivo`,
                `<PortalGuard>` va a canjear el portal y volver a armar la
                sesión entera con `abrirSesion`. Seguir de largo acá pisaría
                eso con un `/me` que todavía habla de la empresa VIEJA — el
                mismo bug que describe la Tarea 10, pero adentro de
                `checkAuth` en vez del guardián. Como `portalActivo` no se
                persiste (a propósito: cada pestaña reconcilia siempre contra
                su URL), esto es lo normal en CADA recarga con una empresa en
                la URL, no un caso raro.
              */
              const slugDeLaUrl = slugDeLaUrlActual();
              if (slugDeLaUrl && slugDeLaUrl !== get().portalActivo) {
                return;
              }

              const token = leerPorPortal<string>(TOKEN_KEY);
              const user = storage.get<User>(USER_KEY);

              if (!token || !user) {
                set({ isAuthenticated: false });
                return;
              }

              // Intentar obtener usuario actual para verificar token
              const currentUser = await authService.getCurrentUser();

              // currentUser may include permissions/menuItems from /me endpoint
              const permissions =
                (currentUser as any).permissions || storage.get<string[]>(PERMISSIONS_KEY) || [];
              const menuItems =
                (currentUser as any).menuItems || storage.get<string[]>(MENU_ITEMS_KEY) || [];
              const lockedMenuItems =
                (currentUser as any).lockedMenuItems ||
                storage.get<string[]>(LOCKED_MENU_ITEMS_KEY) ||
                [];
              storage.set(PERMISSIONS_KEY, permissions);
              storage.set(MENU_ITEMS_KEY, menuItems);
              storage.set(LOCKED_MENU_ITEMS_KEY, lockedMenuItems);
              // El techo también se refresca al recargar: `/me` lo devuelve (si no viene, no se pisa lo guardado).
              if ('tenantModules' in (currentUser as object)) {
                guardarTechoDeLaEmpresa((currentUser as any).tenantModules);
              }
              // Mini Prixia: la marca demo se refresca con cada /me (la plataforma la
              // puede quitar, Tarea 2), en la empresa de ESTE token.
              if (typeof (currentUser as any).esDemo === 'boolean') {
                guardarEmpresaDemo(
                  (currentUser as any).esDemo,
                  slugDeLaClave(TOKEN_KEY),
                  get().isImpersonating
                );
              }

              // Restore organizations
              const orgs: Organization[] =
                (currentUser as any).organizations ||
                storage.get<Organization[]>(ORGANIZATIONS_KEY) ||
                [];
              storage.set(ORGANIZATIONS_KEY, orgs);
              const claveOrgActiva = clavePorPortal(ACTIVE_ORG_KEY, slugDeLaClave(ACTIVE_ORG_KEY));
              const savedOrgId = storage.get<string>(claveOrgActiva);
              const activeOrgId =
                savedOrgId && orgs.some((o) => o.id === savedOrgId) ? savedOrgId : null;
              // Una organización guardada que ya no es de esta persona no se
              // reenvía: el interceptor la leería de ahí en X-Organization-Id.
              if (!activeOrgId && savedOrgId) storage.remove(claveOrgActiva);

              set({
                user: currentUser,
                token,
                isAuthenticated: true,
                permissions,
                // Sólo cuenta si /me trajo la lista: la copia guardada no prueba nada.
                permisosCargados: Array.isArray((currentUser as any).permissions),
                menuItems,
                lockedMenuItems,
                organizations: orgs,
                activeOrganizationId: activeOrgId,
                subscription: (currentUser as any).subscription || null,
              });

              // Sync onboarding completed state from server so modal never re-appears after logout/login
              if ((currentUser as any).onboardingCompleted) {
                const { useOnboardingStore } =
                  await import('@/features/onboarding/store/onboarding-store');
                useOnboardingStore.setState({ completed: true });
              }
            } catch (error) {
              // Si falla, limpiar todo
              get().logout();
            }
          },

          /**
           * Cambiar organización activa
           */
          switchOrganization: (orgId) => {
            // Misma clave por-empresa que lee el interceptor de Axios — si
            // esto quedara sin escoger, el cambio de organización se vería
            // en la pantalla pero el header `X-Organization-Id` seguiría
            // mandando la organización vieja. `slugEfectivo()` y no
            // `portalActivo` por el mismo motivo que en `impersonate`: la
            // barra lateral también se muestra en rutas reservadas como
            // `/admin/...`, donde el guardián no corre y `portalActivo` es
            // `null` — y `null` manda la escritura a la clave global.
            const clave = clavePorPortal(ACTIVE_ORG_KEY, slugEfectivo());
            if (orgId) {
              storage.set(clave, orgId);
            } else {
              storage.remove(clave);
            }
            set({ activeOrganizationId: orgId });
          },

          /**
           * Establecer usuario
           */
          setUser: (user) => {
            storage.set(USER_KEY, user);
            set({ user });
          },

          /**
           * Impersonar usuario (Modo Soporte)
           *
           * 🔴 El token entra bajo `clavePorPortal(TOKEN_KEY, slugEfectivo())`,
           * no la clave global ni la de `portalActivo`: el interceptor de
           * Axios lee la clave por-empresa, y esa clave YA EXISTE (la
           * escribió el login de este mismo admin). Escribir sólo la global
           * no la pisa — cada request seguiría saliendo autenticado como el
           * admin real mientras la pantalla ya muestra a la otra persona.
           *
           * Y no `portalActivo` porque NO SE PERSISTE: entrando derecho a
           * `/admin/users` (escribiéndola o desde un favorito) el guardián
           * ni siquiera corre — es una ruta pública — así que `portalActivo`
           * es `null`, y con `null` `clavePorPortal` devuelve otra vez la
           * clave global. `slugEfectivo()` acierta en las DOS formas de URL
           * que existen de verdad: `/admin/users` (reservada, sin empresa en
           * la URL → la última activa) y `/datec/admin/users` (la que arma la
           * barra lateral bajo el `basename` → `datec`).
           */
          impersonate: async (userId: string) => {
            const state = get();
            const slugDelAdmin = slugEfectivo();
            // Guardar sesión actual del admin
            const backup: ImpersonationBackup = {
              user: state.user!,
              token: state.token!,
              refreshToken: state.refreshToken!,
              permissions: state.permissions,
              menuItems: state.menuItems,
              lockedMenuItems: state.lockedMenuItems,
              organizations: state.organizations,
              activeOrganizationId: state.activeOrganizationId,
              subscription: state.subscription,
              slugDelAdmin,
              demoDelAdmin: window.localStorage.getItem(
                clavePorPortal(EMPRESA_DEMO_KEY, slugDelAdmin)
              ),
            };
            storage.set(IMPERSONATION_KEY, backup);

            // Llamar al endpoint de impersonación
            const response = await apiClient.post(ENDPOINTS.ADMIN_IMPERSONATE(userId));

            /*
              La sesión de la persona impersonada se abre bajo SU empresa, no
              bajo la del admin.

              🔴 Antes esto escribía bajo `slugEfectivo()`, que desde el panel
              (`/plataforma/...`, palabra reservada) cae al último portal DEL
              ADMIN. Mientras el destino posterior era una URL sin empresa
              —`/dashboard` pelado— el defecto no se veía: las dos puntas caían
              al mismo fallback y el token se encontraba de casualidad. En
              cuanto uno navega al portal de esa persona, el interceptor busca
              la clave de ESA empresa y ahí no hay nada: sesión perdida. Y al
              revés, impersonando a alguien de otra empresa, el token quedaba
              escrito en la empresa equivocada.

              `abrirSesion` es el único camino que respeta el invariante de
              `portal-storage.ts` —quien escribe un token escribe la clave que
              el interceptor va a leer para la URL donde va a estar la persona—
              y además mueve el puntero de última empresa, que es lo que hace
              que el destino con empresa resuelva.

              Devuelve el slug para que quien llama sepa a dónde navegar; sin
              eso, el destino vuelve a ser una URL sin empresa y el defecto
              regresa entero.
            */
            const slugDestino = response.user?.tenant?.slug ?? slugDelAdmin;
            abrirSesion(response, slugDestino, true);
            set({ isImpersonating: true });
            return slugDestino;
          },

          /**
           * Salir del modo soporte (volver a cuenta admin)
           *
           * Restaura el token del admin bajo la MISMA clave que `impersonate`
           * sobrescribió: las dos preguntan `slugEfectivo()`, que devuelve la
           * misma empresa mientras la impersonación no CAMBIE de empresa —
           * cambia de identidad dentro de la misma navegación.
           *
           * ⚠️ Impersonar a alguien de OTRA empresa sí la cambia, porque
           * `navigate('/')` termina reconciliando contra la empresa de la
           * persona impersonada. Ahí esto restaura el token del admin bajo
           * la empresa NUEVA y deja la vieja con el token impersonado. Es el
           * frente de ruteo que quedó abierto a propósito (el destino
           * posterior a impersonar no apunta a ninguna empresa en
           * particular): no se arregla desde acá, se arregla decidiendo a
           * dónde navega `admin-users-page.tsx`.
           */
          exitImpersonation: () => {
            const backup = storage.get<ImpersonationBackup>(IMPERSONATION_KEY);
            if (!backup) {
              get().logout();
              return;
            }
            /*
              🔴 La empresa del ADMIN, guardada al entrar — no `slugEfectivo()`.
              Al salir uno está parado en el portal de la persona impersonada,
              así que `slugEfectivo()` devuelve ESA empresa: restaurar ahí deja
              el token del admin en la empresa ajena y el token impersonado
              vivo en la propia. Era el frente que este bloque tenía anotado
              como abierto; ahora el dato existe.
            */
            const slug = backup.slugDelAdmin ?? slugEfectivo();

            // Restaurar sesión del admin
            storage.set(clavePorPortal(TOKEN_KEY, slug), backup.token);
            storage.set(clavePorPortal(REFRESH_TOKEN_KEY, slug), backup.refreshToken);
            if (slug) guardarUltimoPortal(slug);
            storage.set(USER_KEY, backup.user);
            storage.set(PERMISSIONS_KEY, backup.permissions);
            storage.set(MENU_ITEMS_KEY, backup.menuItems);
            storage.set(LOCKED_MENU_ITEMS_KEY, backup.lockedMenuItems || []);
            storage.set(ORGANIZATIONS_KEY, backup.organizations);
            // El techo de quien se suplantó no es el del admin: se vuelve a leer con `checkAuth` (mientras, «no sé»).
            guardarTechoDeLaEmpresa(null);
            storage.remove(IMPERSONATION_KEY);
            // Sólo con el slug con el que se LEYÓ: sin él, la marca leída era la
            // global y escribirla bajo otra empresa la contaminaría.
            if (backup.demoDelAdmin !== undefined && backup.slugDelAdmin) {
              const claveDemo = clavePorPortal(EMPRESA_DEMO_KEY, backup.slugDelAdmin);
              if (backup.demoDelAdmin === null) window.localStorage.removeItem(claveDemo);
              else window.localStorage.setItem(claveDemo, backup.demoDelAdmin);
            }

            set({
              user: backup.user,
              token: backup.token,
              refreshToken: backup.refreshToken,
              permissions: backup.permissions,
              permisosCargados: true,
              menuItems: backup.menuItems,
              lockedMenuItems: backup.lockedMenuItems || [],
              organizations: backup.organizations,
              activeOrganizationId: backup.activeOrganizationId,
              subscription: backup.subscription,
              isAuthenticated: true,
              isImpersonating: false,
            });
          },

          /**
           * Limpiar error
           */
          clearError: () => {
            set({ error: null });
          },

          /**
           * Verificar si el usuario tiene un permiso específico
           * Uses dynamic permissions from server, falls back to hardcoded
           */
          hasPermission: (permission) => {
            const { user, permissions, permisosCargados } = get();
            if (!user) return false;
            // PLATFORM_ADMIN always has everything
            if (user.role === 'PLATFORM_ADMIN') return true;
            /*
              Falla cerrado: sin la lista del servidor no se sabe, y no se supone nada por el
              cargo (la tabla estática se borró: se desactualizaba y mostraba botones que el
              servidor negaba). Una sesión guardada de antes no trae la marca: se refresca.
            */
            if (!permisosCargados) {
              pedirPermisosFrescos(get);
              return false;
            }
            return permissions.includes(permission);
          },

          /**
           * Verificar si el usuario puede ver un item del menú
           */
          canSeeMenu: (menuKey) => {
            const { user, menuItems, permisosCargados } = get();
            if (!user) return false;
            // PLATFORM_ADMIN siempre ve todo
            if (user.role === 'PLATFORM_ADMIN') return true;
            // Si tenemos menuItems del backend, son la fuente de verdad. Con la lista ya
            // cargada, un menú vacío es «no ve ningún módulo» (mismo criterio que
            // `hasPermission`): el administrador no ve el menú entero por ser administrador.
            if (menuItems.length > 0 || permisosCargados) {
              return menuItems.includes(`menu:${menuKey}`);
            }
            // Fallback restrictivo: si no tenemos info, COMPANY_ADMIN ve todo,
            // cualquier otro rol no ve nada. Antes era "ver todo" para todos,
            // lo que abría el sidebar entero cuando la persist no traia menuItems.
            return user.role === 'COMPANY_ADMIN';
          },

          /**
           * Verificar si un módulo está bloqueado (visible pero no usable).
           */
          isModuleLocked: (menuKey) => {
            const { user, lockedMenuItems } = get();
            if (!user) return false;
            // PLATFORM_ADMIN nunca tiene módulos bloqueados.
            if (user.role === 'PLATFORM_ADMIN') return false;
            return lockedMenuItems.includes(`menu:${menuKey}`);
          },
        };
      },
      {
        name: AUTH_PERSIST_KEY,
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
          permissions: state.permissions,
          permisosCargados: state.permisosCargados,
          menuItems: state.menuItems,
          lockedMenuItems: state.lockedMenuItems,
          organizations: state.organizations,
          activeOrganizationId: state.activeOrganizationId,
          subscription: state.subscription,
          isImpersonating: state.isImpersonating,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.user?.tenant?.decimalPlaces != null) {
            setGlobalDecimalPlaces(state.user.tenant.decimalPlaces);
          }
        },
      }
    ),
    { name: 'AuthStore' }
  )
);

export default useAuthStore;
